import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TabBase } from '@core/base/tab.base';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvDynamicFormUtils, DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import DateUtils from '@shared/utils/date.utils';
import { filter, map, Observable, take, tap } from 'rxjs';

import { VehicleMileageForm } from './interfaces';
import { VehicleMileageTabService } from './vehicle-mileage-tab.service';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
@Component({
  templateUrl: './vehicle-mileage-tab.component.html',
  providers: [VehicleMileageTabService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehicleMileageTabComponent
  extends TabBase
  implements PendingChanges, OnInit
{
  private agGridEntityService = inject(SplitterWithGridHelperService);
  private api = inject(RestApiService);
  readonly tab = inject(VehicleMileageTabService);
  private permissionsService = inject(UserRolesAndPermissionsService);

  elements: DvDynamicFormElement[];
  formGroup: FormGroup<FormOf<VehicleMileageForm>>;

  private destroyRef = inject(DestroyRef);

  constructor() {
    super(inject(ActivatedRoute).parent!);
  }

  ngOnInit(): void {
    this.elements = this.tab.elements;
    this.formGroup = DvFormUtils.createFormGroup(this.elements);
    this.subscribeToSelectedEntityId();
    this.subscribeToAdjustedMileageChanges();
  }

  subscribeToSelectedEntityId(): void {
    this.entityId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      if (!id) return;
      const keys = DvDynamicFormUtils.getKeysForRequest(this.elements);
      this.api
        .getInfo<VehicleMileageForm>(Entity.Vehicle, id, keys)
        .subscribe(data => {
          // for every entity default value will be current date time
          const currentDateTime = new Date();
          data.MileageLastAdjustment =
            DateUtils.convertDate(currentDateTime) +
            'T' +
            (currentDateTime.getHours() < 10 ? '0' : '') +
            currentDateTime.getHours() +
            ':' +
            (currentDateTime.getMinutes() < 10 ? '0' : '') +
            currentDateTime.getMinutes() +
            ':' +
            (currentDateTime.getSeconds() < 10 ? '0' : '') +
            currentDateTime.getSeconds();

          this.formGroup.reset();
          this.formGroup.patchValue(data);
          if (!data.ShowRecord) {
            this.formGroup.disable();
          } else {
            this.formGroup.enable();
            this.formGroup.controls.mileageDifference.disable();
            this.formGroup.controls.Mileage.disable();
          }
          this.handleAccessRestrictions();
        });
    });
  }

  subscribeToAdjustedMileageChanges(): void {
    const { controls } = this.formGroup;
    controls.adjustedMileage.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        const mileageOnStartupControl =
          this.formGroup.controls.mileageOnStartup;
        val
          ? mileageOnStartupControl.enable()
          : mileageOnStartupControl.disable();
        const newMileage = typeof val === 'string' ? parseInt(val) : val;
        const diff = newMileage - controls.Mileage.value;
        controls.mileageDifference.setValue(diff);
      });
  }

  hasChanges = (): boolean => this.formGroup.dirty && this.formGroup.valid;
  pendingChangesSave = (): Observable<boolean> => this.performSave();

  onSave(): void {
    this.performSave().subscribe();
  }

  private performSave(): Observable<boolean> {
    const entityId = this.entityId$.value;
    const data = this.formGroup.value;
    return this.api.put(Entity.Vehicle, entityId, data).pipe(
      tap(() => {
        this.formGroup.markAsPristine();
        this.agGridEntityService
          .refreshRowData<VehicleMileageForm>(entityId)
          .pipe(
            filter(updatedEntity => !!updatedEntity),
            take(1)
          )
          .subscribe(updatedEntity => {
            this.formGroup.reset();
            this.formGroup.patchValue(updatedEntity, { emitEvent: false });
          });
      }),
      map(() => true)
    );
  }

  private handleAccessRestrictions(): void {
    this.permissionsService
      .hasPermissions([RolesAndPermissions.VehicleAdjustMileageEdit])
      .pipe(take(1))
      .subscribe(hasPermission => {
        if (!hasPermission) {
          this.formGroup.disable();
        }
      });
  }
}
